import { action, extendObservable, observable } from 'mobx';
import { apiFetchGet, apiFetchUrlGet, apiFetchPostUrlEncoded } from '../helpers/api';
import store from './store';
import { isKeyExists, LS_GOVERNANCE } from '../helpers/localStorage';

import {
    GET_COMPANIES,
    GET_GROUPS,
    COMPANY_COLOR_VAR,
    GROUP_COLOR_VAR,
    DEFAULT_COMPANY_COLOR,
    PAIR_COMPANY,
    REQUEST_GOVERNANCE,
    GOV_DEFAULT_TITLE,
    DOC_TYPE_GOVERNANCE,
} from '../helpers/constants';
import { setColor, getUnix } from '../helpers/utils';
import _ from 'lodash';

export default class CompaniesStore {
    constructor(factory) {
        this.companyUpdateDelegate = null;
        this.validHierarchy = [];
        this.validDocuments = [];

        extendObservable(this, {
            data: [],
            logos: [],
            selectedIndex: null,
            collapsed: false,
            groups: [],
            routeBeforeRefresh: '',
            loadingGovernance: false,

            set: action((field, value) => {
                this[field] = value;                             
            }),

            getColorByIndex(index) {
                return this.data.Companies[index].Hex;
            },

            getCompanyByIndex(index = this.selectedIndex) {
                return _.get(this.data, 'Companies[' + index + ']', null);
            },

            getCompanyIndexById(instanceId) {
                for (var i = 0; i < this.data.Companies.length; i++) {
                    if (this.data.Companies[i].InstanceId === instanceId) {
                        return i;
                    }
                }
            },

            getNameByIndex(index = this.selectedIndex) {
                return _.get(this.data, 'Companies[' + index + '].DisplayName', '');
            },

            setLogo: action((index, clientId, url) => {
                //
                this.logos[index] = { clientId, url };
            }),

            get selectedCompany() {
                return _.get(this.data, 'Companies[' + this.selectedIndex + ']', null);
            },

            setCompanyHasNewItems: action((company, value) => {
                company.hasNewItems = value;
            }),

            getCompanyById(Id) {
                return _.find(this.data.Companies, c => {
                    return c.InstanceId === Id;
                });
            },

            initLogosAndGroups: action(i => {
                this.logos = new Array(i);
                this.groups = new Array(i);
            }),

            select: action(index => {
                this.selectedIndex = index;

                //toggle the meeting tab as needed
                this.factory.meetings.filterMeetingsByHierarchy();
                this.factory.meetings.checkForMeetings();

                setColor(COMPANY_COLOR_VAR, this.data.Companies[index].Hex);
                // document.documentElement.style.setProperty("--company-color", this.data.Companies[index].Hex);
            }),

            toggleSelected: action(index => {
                if (this.selectedIndex === null) {
                    // DO NOT CHANGE THIS
                    this.selectedIndex = index;
                    // check number of groups
                    if (this.groups[index].Groups.length === 1) {
                        //
                        this.factory.groups.selectedId = this.groups[index].Groups[0].GroupId;
                        this.factory.groups.governanceTitle = this.groups[
                            index
                        ].Groups[0].GovernanceTitle;
                    }
                    setColor(COMPANY_COLOR_VAR, this.data.Companies[index].Hex);
                    setColor(GROUP_COLOR_VAR, this.data.Companies[index].Hex);
                } else {
                    if (this.selectedIndex === index) {
                        // DO NOT CHANGE THIS
                        this.selectedIndex = null;
                        this.factory.groups.selectedId = null;
                        this.factory.groups.governanceTitle = '';
                        setColor(COMPANY_COLOR_VAR, DEFAULT_COMPANY_COLOR);
                        setColor(GROUP_COLOR_VAR, DEFAULT_COMPANY_COLOR);
                    } else {
                        this.selectedIndex = index;
                        this.factory.groups.selectedId = null;
                        this.factory.groups.selectedId = null;
                        if (this.groups[index].Groups.length === 1) {
                            this.factory.groups.selectedId = this.groups[index].Groups[0].GroupId;
                            this.factory.groups.governanceTitle = this.groups[
                                index
                            ].Groups[0].GovernanceTitle;
                        }

                        setColor(COMPANY_COLOR_VAR, this.data.Companies[index].Hex);
                        setColor(GROUP_COLOR_VAR, this.data.Companies[index].Hex);
                    }
                }

                //toggle the meeting tab as needed
                this.factory.meetings.filterMeetingsByHierarchy();
                this.factory.meetings.checkForMeetings();
                this.factory.votes.refreshShouldDisplayVoteTab();
                this.factory.tasks.refreshShouldDisplayTaskTab();

                this.factory.tasks.clearAllFilters();
                this.factory.tasks.currentFilterSelection = 0;
                this.factory.tasks.currentColumnOrder = 'asc';
            }),

            setCompanies: action(json => {
                //if no companies, log out the user (can happen when refreshing)
                if (json.Companies.length === 0) {
                    this.factory.user.logout();
                }

                //clean the indexedDB valid arrays ready for use
                this.validHierarchy = [];
                this.validDocuments = [];

                json.Companies = this.sortAZ(json.Companies);
                json.Companies.forEach((company, i) => {
                    company.hasNewItems = false;
                });
                this.data = json;

                this.initLogosAndGroups(json.Companies.length); // MobX will give an error if we set to index which not exist
                this.loadLogos(json.Companies);
                this.fetchGroups(json.Companies);               // this.fetchGroups completed when store.groups.loadingGov = true
                this.factory.meetings.fetch();                  // meetings.fetch completed when store.meetings.loading = false
                this.factory.votes.fetch();                     // votes.fetch completed with store.votes.loading = false
                this.factory.tasks.fetch();
                this.factory.tasks.fetchTaskUsers();
                this.factory.tasks.fetchTaskCategories();
                this.factory.war.fetch();                       // war.fetch completed with store.war.loading = false
            }),

            get selectedId() {
                if (
                    this.selectedIndex !== null &&
                    this.data.Companies[this.selectedIndex] !== undefined
                ) {
                    return this.data.Companies[this.selectedIndex].InstanceId;
                }
                return null;
            },

            indexFromId: id => {
                let index = null;
                this.data.Companies.forEach((company, i) => {
                    if (company.InstanceId.toString() === id.toString()) {
                        index = i;
                    }
                });
                return index;
            },

            fetch: () => {
                return apiFetchGet(GET_COMPANIES, this.factory.user.token).then(json => {
                    this.setCompanies(json);
                });
            },

            loadLogos: companies => {
                companies.forEach((company, i) => {
                    this.loadLogo(company.LogoUrl).then(url => {
                        // better to store it to local db
                        //
                        this.setLogo(i, company.InstanceId, url);
                    });
                });
            },
            togglePanel: action(() => {
                this.collapsed = !this.collapsed;
            }),

            fetchGroups: companies => {
                this.factory.groups.setGovLoading(false);
                store.companies.set('loadingGovernance', true);

                Promise.all(
                    companies.map((company, i) => {
                        return this.fetchGroupsOfCompany(company.InstanceId).then(group => {
                            return this.setGroups(company.InstanceId, group, i).then(() => {
                                return;
                            });
                        });
                    }),
                ).then(() => {
                    this.factory.groups.setGovLoading(true);
                    this.factory.groups.setSelectedDocuments();
                    this.onGroupsFetch();

                    if (window.offlineApiExtension) {
                        window.offlineApiExtension.cleanIdbContent_Hierarchy(this.validHierarchy);
                        window.offlineApiExtension.cleanIdbContent_Documents(
                            store.documents.buildCompareString(DOC_TYPE_GOVERNANCE),
                            this.validDocuments,
                        );
                    }
                });

                store.companies.set('loadingGovernance', false);
            },

            setGroups: action((instanceId, groups, index) => {
                //need a blank item to ensure "cleanContentInDB" runs as expected
                this.validHierarchy.push({ instanceId, groupId: 0 });
                
                return Promise.all(
                    groups.Groups.map((group, i) => {
                        //build the company/group hierarchy ready to clean indexedDB
                        this.validHierarchy.push({ instanceId, groupId: group.GroupId });

                        group.groupsIndex = index;
                        let lastActivity = groups.GroupActivity.filter(
                            gr => gr.GroupId === group.GroupId,
                        )[0].LastTouchedDateTime;
                        group.lastActivity = getUnix(lastActivity);
                        group.hasNewItems = false;
                        let company = this.getCompanyByIndex(index);
                        group.company = company;

                        this.fetchAllMemberAvatarForGroup(group, index);

                        return this.fetchGovernance(group).then(categories => {
                            this.setGovernance(categories.CategoryList, group.GroupId, index);
                        });
                    }),
                    (this.groups[index] = groups),
                );
            }),

            setGovernance: action((categories, groupId, companyIndex) => {
                let groups = store.groups.getGroupsByCompanyIndex(companyIndex);
                if (groups !== undefined) {
                    let group = _.find(groups.Groups, g => {
                        return g.GroupId === groupId;
                    });
                    this.setCategoryChildrenValue(categories);

                    this.processCategoryDocs(categories, group, companyIndex);
                    group.governanceCategories = observable(categories);
                    store.groups.setVisualIndicatorsToGroupsAndCompanies();
                }
            }),

            fetchGroupsOfCompany: companyId => {
                return apiFetchGet(GET_GROUPS, this.factory.user.token, {
                    instanceId: companyId,
                }).then(json => {
                    return json;
                });
            },

            fetchAllMemberAvatarForGroup: (group, index) => {
                group.Members.map(member => {
                    //
                    if (!member.AvatarUrl) return null;
                    return apiFetchUrlGet(member.AvatarUrl, this.factory.user.token).then(json => {
                        if (json !== null) {
                            this.setMemberAvatarUrl(group.GroupId, index, member.UserId, json);
                        }
                    });
                });
            },

            setMemberAvatarUrl: action((groupId, index, userId, json) => {
                let groups = this.groups[index];
                let group = groups.Groups.filter(item => {
                    return item.GroupId === groupId;
                })[0];
                let member = group.Members.filter(item => {
                    return item.UserId === userId;
                })[0];
                member.localAvatarUrl = json;
            }),

            fetchGovernance: group => {
                return apiFetchGet(REQUEST_GOVERNANCE, this.factory.user.token, {
                    id: group.company.InstanceId,
                    groupId: group.GroupId,
                }).then(json => {
                    return json;
                });
            },

            setCategoryChildrenValue: flat => {
                var all = {};
                flat.forEach(function(item) {
                    all[item.GovCatId] = item;
                });

                Object.keys(all).forEach(function(GovCatId) {
                    var item = all[GovCatId];

                    if (item.ParentId !== 0) {
                        var p = all[item.ParentId];
                        p.children = true;
                    }
                });
            },

            processCategoryDocs: (categories, group, companyIndex) => {
                let numberOfUnreadDocuments = 0;
                let arrValidDocuments = [];
                const company = this.getCompanyByIndex(companyIndex);

                categories.forEach(function(category) {
                    category.HasUnreadDocuments = false;
                    if (category.NumberOfDocuments > 0) {
                        category.GovernanceDocs.forEach(function(document) {
                            if (
                                !isKeyExists(
                                    LS_GOVERNANCE +
                                        company.InstanceId.toString() +
                                        group.GroupId.toString() +
                                        store.user.profile[0].UserId.toString(),
                                    document.GovDocId,
                                )
                            ) {
                                document.IsRead = false;
                                category.HasUnreadDocuments = true;
                                numberOfUnreadDocuments++;
                            } else {
                                document.IsRead = true;
                            }

                            //build the valid doc key list ready to clean indexedDB
                            const docKey = store.documents.buildDocKey(
                                DOC_TYPE_GOVERNANCE,
                                company.InstanceId.toString(),
                                document.GovDocId.toString(),
                            );
                            arrValidDocuments.push({ docKey });
                        });
                    }
                });

                group.NumberOfUnreadDocuments = numberOfUnreadDocuments;

                if (arrValidDocuments.length > 0) {
                    this.validDocuments = this.validDocuments.concat(arrValidDocuments);
                }
                return categories;
            },
        });
        this.factory = factory;
    }

    pairCompany = authToken => {
        const body = 'FriendlyToken=' + authToken;
        return apiFetchPostUrlEncoded(PAIR_COMPANY, this.factory.user.token, body).then(json => {
            return json;
        });
    };

    loadLogo = imageUrl => {
        if (!imageUrl) return new Promise(resolve => null);
        return apiFetchUrlGet(imageUrl, store.user.token).then(json => {
            return json;
        });
    };

    // sort alphabetically
    sortAZ = array => {
        return array.sort((a, b) => {
            return a.DisplayName > b.DisplayName;
        });
    };

    /**
     * It runs on agenda display to set button text for governance button toggle
     * @param  {number} instanceId company Id
     * @param  {number} groupId group Id
     * @return {any}         String to be displayed as a button text
     */
    getGroupGovernanceTitle = (instanceId, groupId) => {
        const index = this.indexFromId(instanceId);
        const company = this.getCompanyByIndex(index);

        const group = _.find(company.Groups, g => {
            return g.GroupId === groupId;
        });

        if (group !== null) {
            return group.GovernanceTitle;
        } else {
            return GOV_DEFAULT_TITLE;
        }
    };

    // hooks
    onGroupsFetch = () => {
        if (this.data.Companies.length === 1) {
            //check first if the single company is already selected
            if (this.selectedIndex !== 0) {
                this.toggleSelected(0);
            }
        }

        //also need to check if the selected group still exists, if not, trigger a "toggle" so that the ContentMenu is refreshed
        let group = _.find(this.validHierarchy, g => {
            return g.groupId === this.factory.groups.selectedId;
        });
        if (this.factory.groups.selectedId !== null && group === undefined) {
            this.factory.groups.toggleSelected(this.factory.groups.selectedId, 0);
        }
    };
}



// WEBPACK FOOTER //
// ./src/stores/CompaniesStore.js